"use client"
import React, {
  ElementRef,
  forwardRef,
  MouseEventHandler,
  useMemo,
} from "react"
import { tv, VariantProps } from "tailwind-variants"
import { classNames } from "../../utils"
import { Block, BlockProps } from "../Block"
import { InlineFlex } from "../InlineFlex"
import { tableRowSizeVariants } from "./constants"
import { TableCell } from "./TableCell"
import { TableRowContext } from "./TableRowContext"
import { useTable } from "./useTable"

type Props = {
  className?: string
  children: React.ReactNode
  onClick?: MouseEventHandler<HTMLElement>
} & Omit<BlockProps, "asChild"> &
  VariantProps<typeof tableRowVariants>

const tableRowVariants = tv({
  variants: {
    interactive: {
      true: classNames(
        "group cursor-pointer hover:bg-bg-additional-1",
        "active:bg-bg-additional-2 *:bg-transparent",
      ),
    },
    interactiveTransparent: {
      true: classNames(
        "group cursor-pointer hover:bg-bg-additional-1-transparent",
        "active:bg-bg-additional-2-transparent *:bg-transparent",
      ),
    },
  },
})

const TableRowBase = forwardRef<ElementRef<typeof Block>, Props>(
  function TableRowBase(
    {
      className,
      children,
      onClick,
      interactive: paramInteractive,
      interactiveTransparent,
      ...rest
    }: Props,
    ref,
  ) {
    const { size, dividers } = useTable()
    const interactive = paramInteractive ?? Boolean(onClick)
    const value = useMemo(() => ({ interactive }), [interactive])

    return (
      <TableRowContext.Provider value={value}>
        <InlineFlex
          className={classNames(
            "w-max min-w-full items-center gap-4 relative",
            dividers ? "border-b border-border-1" : "rounded",
            tableRowVariants({ interactive, interactiveTransparent }),
            tableRowSizeVariants({ size }).base(),
            className,
          )}
          ref={ref}
          role="row"
          onClick={onClick}
          {...rest}
        >
          {children}
        </InlineFlex>
      </TableRowContext.Provider>
    )
  },
)

export const TableRow = Object.assign(TableRowBase, {
  Cell: TableCell,
})
